import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import DataList from '../../components/DataList';
import useCheckPermission from '../../hooks/useCheckPermission';
import authorizationAuditService from './authorizationAuditService';
import tenantService from './tenantService';
import { TenantStatus } from './TenantForm';
import platformUsageService, {
  formatBytes,
  formatCount,
  formatUsageDate,
  deviceUtilizationPct,
  severityColor,
  readContentInventory,
} from '../platform/platformUsageService';
import UsagePeriodPicker from '../../components/saas/UsagePeriodPicker';
import {
  BillingContext,
  UsagePeriodValue,
  createDefaultUsagePeriod,
  formatPeriodLabel,
} from '../../components/saas/usagePeriod';

const ACCESS_LEVEL_LABELS: Record<number, string> = {
  0: 'Engedélyezett',
  1: 'Türelmi idő',
  2: 'Blokkolva',
};

const TenantOverviewPage = () => {
  const params = useParams();
  const tenantId = params.id;
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const { canPlatform } = useCheckPermission();
  const canManage = canPlatform(['ManageTenants']);

  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState<any>(null);
  const [audits, setAudits] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [usage, setUsage] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usagePeriod, setUsagePeriod] = useState<UsagePeriodValue>(() =>
    createDefaultUsagePeriod()
  );
  const [billingPeriodSeeded, setBillingPeriodSeeded] = useState(false);

  const loadOverview = () => {
    if (!tenantId) return;
    dispatch({ type: 'SHOW_QUERY' });
    tenantService
      .getOverview(tenantId)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), { variant: 'error' });
        } else {
          setOverview(response.result);
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const loadMembers = () => {
    if (!tenantId) return;
    tenantService.listMembers(tenantId).then((response) => {
      if (!response.hasError) {
        setMembers(
          (response.records ?? []).map((x: any, index: number) => ({
            ...x,
            id: x.membershipId ?? x.MembershipId ?? index,
            roleTemplateNames: (
              x.roleTemplateNames ??
              x.RoleTemplateNames ??
              []
            ).join(', '),
          }))
        );
      }
    });
  };

  const loadAudits = () => {
    if (!tenantId) return;
    authorizationAuditService.list(tenantId).then((response) => {
      if (!response.hasError) setAudits(response.records ?? []);
    });
  };

  const loadUsage = useCallback(
    (opts?: { silent?: boolean; period?: UsagePeriodValue }) => {
      if (!tenantId) return;
      const period = opts?.period ?? usagePeriod;
      if (!opts?.silent) setUsageLoading(true);
      setUsageError(null);
      platformUsageService
        .getTenantUsage(tenantId, { from: period.from, to: period.to })
        .then((response) => {
          if (response.hasError) {
            setUsage(null);
            setUsageError(
              (response.errorMessages ?? []).join(', ') ||
                'Használati adatok betöltése sikertelen'
            );
          } else {
            setUsage(response.result);
          }
        })
        .finally(() => setUsageLoading(false));
    },
    [tenantId, usagePeriod]
  );

  useEffect(() => {
    loadOverview();
    setBillingPeriodSeeded(false);
    setUsagePeriod(createDefaultUsagePeriod());
  }, [tenantId]);

  useEffect(() => {
    loadUsage({ silent: true, period: usagePeriod });
  }, [tenantId, usagePeriod]);

  useEffect(() => {
    if (tab === 3) loadMembers();
    if (tab === 5) loadAudits();
  }, [tab, tenantId]);

  const subscription = overview?.subscription;
  const billingContext = useMemo((): BillingContext | null => {
    if (!subscription) return null;
    const startsAt = subscription.startsAt ?? subscription.StartsAt ?? null;
    if (!startsAt) return null;
    return {
      startsAt,
      endsAt: subscription.endsAt ?? subscription.EndsAt ?? null,
      paymentOccurrence:
        subscription.paymentOccurrence ??
        subscription.PaymentOccurrence ??
        'Monthly',
    };
  }, [subscription]);

  useEffect(() => {
    if (billingPeriodSeeded || !billingContext?.startsAt) return;
    setBillingPeriodSeeded(true);
    setUsagePeriod(createDefaultUsagePeriod(billingContext));
  }, [billingContext, billingPeriodSeeded]);

  const handleImpersonate = () => {
    if (!tenantId) return;
    dispatch({ type: 'SHOW_SAVE' });
    tenantService
      .impersonate(tenantId)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), { variant: 'error' });
        } else {
          enqueueSnackbar('Bérlő kontextus aktiválva', { variant: 'success' });
          window.location.href = '/';
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  if (!tenantId) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Hiányzó bérlő azonosító.</Typography>
      </Paper>
    );
  }

  const tenant = overview?.tenant ?? overview;
  const entitlements = overview?.entitlements ?? overview?.Entitlements ?? [];
  const memberCount = overview?.memberCount ?? overview?.MemberCount ?? 0;
  const branding = overview?.branding ?? overview?.Branding;
  const checklist = overview?.checklist ?? overview?.Checklist ?? [];
  const accessLevel = overview?.accessLevel ?? overview?.AccessLevel ?? 0;
  const usageAlerts = usage?.alerts ?? usage?.Alerts ?? [];
  const usageCurrent = usage?.current ?? usage?.Current ?? {};

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5">
                Bérlő 360 — {tenant?.name ?? tenant?.Name ?? tenantId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tenant?.slug ?? tenant?.Slug} · {tenantId}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {canManage && (
                <Button variant="outlined" onClick={handleImpersonate}>
                  Belépés bérlőként
                </Button>
              )}
              <Button
                variant="outlined"
                component={RouterLink}
                to={`/tenants/edit/${tenantId}`}
              >
                Szerkesztés
              </Button>
              <Button variant="text" onClick={() => navigate('/tenants')}>
                Vissza a listához
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid size={12}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Áttekintés" />
            <Tab label="Előfizetés" />
            <Tab label="Entitlementek" />
            <Tab label="Tagok" />
            <Tab label="Branding / API" />
            <Tab label="Audit" />
            <Tab label="Használat" />
          </Tabs>
        </Grid>

        {tab === 0 && (
          <Grid size={12}>
            {!overview && (
              <Typography color="text.secondary">Betöltés…</Typography>
            )}
            {overview && (
              <Grid container spacing={2}>
                {usageAlerts.length > 0 && (
                  <Grid size={12}>
                    {(usageAlerts as any[]).map((alert, idx) => (
                      <Alert
                        key={idx}
                        severity={
                          alert.severity === 'critical' ||
                          alert.Severity === 'critical'
                            ? 'error'
                            : 'warning'
                        }
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => setTab(6)}
                          >
                            Használat
                          </Button>
                        }
                        sx={{ mb: idx < usageAlerts.length - 1 ? 1 : 0 }}
                      >
                        {alert.message ?? alert.Message}
                      </Alert>
                    ))}
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Hozzáférés szint</Typography>
                    <Chip
                      label={ACCESS_LEVEL_LABELS[accessLevel] ?? accessLevel}
                      color={accessLevel === 2 ? 'error' : accessLevel === 1 ? 'warning' : 'success'}
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Státusz</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {tenant?.status === TenantStatus.Suspended
                        ? 'Felfüggesztve'
                        : tenant?.status === TenantStatus.PendingDeletion
                          ? 'Törlésre vár'
                          : tenant?.status === TenantStatus.Archived
                            ? 'Archivált'
                            : 'Aktív'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Tagok</Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>{memberCount}</Typography>
                  </Paper>
                </Grid>
                {checklist.length > 0 && (
                  <Grid size={12}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Ellenőrző lista</Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      {checklist.map((item: any, idx: number) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={item.done ?? item.Done ? '✓' : '○'}
                            color={item.done ?? item.Done ? 'success' : 'default'}
                          />
                          <Typography variant="body2">
                            {item.label ?? item.Label ?? item.key ?? item.Key}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        )}

        {tab === 1 && (
          <Grid size={12}>
            {!subscription && (
              <Alert severity="info">Nincs aktív előfizetés vagy még betöltés alatt.</Alert>
            )}
            {subscription && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  <strong>Név:</strong> {subscription.name ?? subscription.Name}
                </Typography>
                <Typography variant="body2">
                  <strong>Státusz:</strong> {subscription.status ?? subscription.Status}
                </Typography>
                <Typography variant="body2">
                  <strong>Licenc:</strong>{' '}
                  {subscription.licenseName ??
                    subscription.LicenseName ??
                    subscription.licenseId ??
                    subscription.LicenseId}
                </Typography>
                <Typography variant="body2">
                  <strong>Fizetés:</strong>{' '}
                  {subscription.paymentOccurrence ?? subscription.PaymentOccurrence}
                </Typography>
                <Typography variant="body2">
                  <strong>Kezdés:</strong>{' '}
                  {subscription.startsAt ?? subscription.StartsAt ?? '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Lejárat:</strong>{' '}
                  {subscription.endsAt ?? subscription.EndsAt ?? '—'}
                </Typography>
                {canManage && (
                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    component={RouterLink}
                    to={`/subscriptions/edit/${subscription.id ?? subscription.Id}`}
                  >
                    Előfizetés szerkesztése
                  </Button>
                )}
              </Paper>
            )}
          </Grid>
        )}

        {tab === 2 && (
          <Grid size={12}>
            <DataList
              rows={(entitlements ?? []).map((e: any, i: number) => ({
                id: e.id ?? e.Id ?? i,
                moduleCode: e.moduleCode ?? e.ModuleCode,
                source: e.source ?? e.Source,
                isActive: e.isActive ?? e.IsActive,
              }))}
              columns={[
                { field: 'moduleCode', headerName: 'Modul', flex: 120 },
                { field: 'source', headerName: 'Forrás', flex: 80 },
                {
                  field: 'isActive',
                  headerName: 'Aktív',
                  flex: 60,
                  valueFormatter: (v: boolean) => (v ? 'Igen' : 'Nem'),
                },
              ]}
            />
          </Grid>
        )}

        {tab === 3 && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6">
                  Tagok ({memberCount})
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to="/invitations"
                  >
                    Meghívás (SaaS)
                  </Button>
                </Box>
              </Box>
              <DataList
                rows={members}
                columns={[
                  { field: 'fullName', headerName: 'Név', flex: 120 },
                  { field: 'email', headerName: 'Email', flex: 140 },
                  { field: 'status', headerName: 'Státusz', flex: 80 },
                  {
                    field: 'roleTemplateNames',
                    headerName: 'Szerepkörök',
                    flex: 140,
                  },
                  {
                    field: 'isDefault',
                    headerName: 'Alapértelmezett',
                    flex: 80,
                    valueGetter: (_v: any, row: any) =>
                      row.isDefault ? 'Igen' : '',
                  },
                ]}
              />
            </Paper>
          </Grid>
        )}

        {tab === 4 && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              {branding && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Branding összefoglaló</Typography>
                  <Typography variant="body2">
                    Terméknév: {branding.productName ?? branding.ProductName ?? '—'}
                  </Typography>
                  <Typography variant="body2">
                    Belépő szöveg: {branding.loginTitle ?? branding.LoginTitle ?? '—'}
                  </Typography>
                  <Typography variant="body2">
                    Színek: {branding.primaryColor ?? branding.PrimaryColor ?? '—'} /{' '}
                    {branding.secondaryColor ?? branding.SecondaryColor ?? '—'}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A teljes branding, működési beállítások és API kulcs kezelés a bérlő admin
                felületen érhető el.
              </Typography>
              <Button
                variant="contained"
                component={RouterLink}
                to={`/tenants/${tenantId}/admin`}
              >
                Bérlő platform beállítások megnyitása
              </Button>
            </Paper>
          </Grid>
        )}

        {tab === 5 && (
          <Grid size={12}>
            <DataList
              rows={audits.map((a, i) => ({
                id: a.id ?? a.Id ?? i,
                action: a.action ?? a.Action,
                targetType: a.targetType ?? a.TargetType,
                targetId: a.targetId ?? a.TargetId,
                timestamp: a.timestamp ?? a.Timestamp,
                actorUserId: a.actorUserId ?? a.ActorUserId,
              }))}
              columns={[
                { field: 'timestamp', headerName: 'Időpont', flex: 120 },
                { field: 'action', headerName: 'Művelet', flex: 120 },
                { field: 'targetType', headerName: 'Típus', flex: 80 },
                { field: 'targetId', headerName: 'Cél', flex: 100 },
                { field: 'actorUserId', headerName: 'Felhasználó', flex: 80 },
              ]}
            />
          </Grid>
        )}

        {tab === 6 && (
          <Grid size={12}>
            <Box sx={{ mb: 2 }}>
              <UsagePeriodPicker
                value={usagePeriod}
                onChange={setUsagePeriod}
                billing={billingContext}
                disabled={usageLoading}
              />
            </Box>
            {usageLoading && !usage && (
              <Typography color="text.secondary">Betöltés…</Typography>
            )}
            {usageError && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => loadUsage()}
                  >
                    Újra
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                {usageError}
              </Alert>
            )}
            {usage && (
              <Grid container spacing={2}>
                {(usageAlerts as any[]).map((alert, idx) => (
                  <Grid key={`alert-${idx}`} size={12}>
                    <Alert
                      severity={
                        alert.severity === 'critical' ||
                        alert.Severity === 'critical'
                          ? 'error'
                          : 'warning'
                      }
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {alert.message ?? alert.Message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Üzemeltetői riasztás a licenc MaxDevices értékéhez — a
                        szolgáltatás nincs blokkolva.
                      </Typography>
                    </Alert>
                  </Grid>
                ))}
                {(() => {
                  const current = usageCurrent;
                  const maxDevices = Number(current.maxDevices ?? 0);
                  const installed = Number(current.installedDevices ?? 0);
                  const active = Number(current.activeDevices30d ?? 0);
                  const deviceCurrent = Math.max(installed, active);
                  const devicePct = deviceUtilizationPct(deviceCurrent, maxDevices);
                  const alertSeverity =
                    (usageAlerts as any[])[0]?.severity ??
                    (usageAlerts as any[])[0]?.Severity;
                  const progressColor =
                    devicePct == null
                      ? 'primary'
                      : severityColor(
                          alertSeverity ??
                            (devicePct >= 100
                              ? 'critical'
                              : devicePct >= 80
                                ? 'warning'
                                : null)
                        );

                  return (
                    <>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Typography variant="subtitle2">
                            Eszközök
                          </Typography>
                          <Typography variant="h4" sx={{ mt: 1 }}>
                            {formatCount(active)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Aktív gördülő 30 nap · telepített:{' '}
                            {formatCount(installed)}
                            {maxDevices > 0
                              ? ` · max: ${formatCount(maxDevices)}`
                              : ' · nincs MaxDevices a licencen'}
                          </Typography>
                          {devicePct != null && (
                            <Box sx={{ mt: 1.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, devicePct)}
                                color={progressColor}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {devicePct}% a MaxDevices értékhez képest
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Typography variant="subtitle2">
                            Mobil API (időszak)
                          </Typography>
                          <Typography variant="h4" sx={{ mt: 1 }}>
                            {formatCount(current.apiCallsMobile)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatPeriodLabel(usagePeriod)}
                            {Number(current.apiCallsCms) > 0
                              ? ` · CMS: ${formatCount(current.apiCallsCms)}`
                              : ''}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Typography variant="subtitle2">Tárhely</Typography>
                          <Typography variant="h4" sx={{ mt: 1 }}>
                            {formatBytes(
                              current.storageBytes ?? current.StorageBytes
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Galéria + CMS blob ledger (létező blobok)
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Typography variant="subtitle2">Tartalom</Typography>
                          <Typography variant="h4" sx={{ mt: 1 }}>
                            {formatCount(current.contentTotal)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Cikk: {formatCount(current.articles)} · Tag:{' '}
                            {formatCount(current.memberCount)} · App profil:{' '}
                            {formatCount(current.releaseProfileCount)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1.5 }}
                          >
                            Mobilapp tartalomtípusok
                          </Typography>
                          <Grid container spacing={1}>
                            {readContentInventory(usage).map((row) => (
                              <Grid
                                key={row.key}
                                size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                              >
                                <Box
                                  sx={{
                                    px: 1.25,
                                    py: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    {row.label}
                                  </Typography>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    {formatCount(row.count)}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      </Grid>
                      <Grid size={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Stack
                            direction="row"
                            sx={{
                              mb: 1.5,
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography variant="subtitle2">
                              Napi idősor · {formatPeriodLabel(usagePeriod)}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => loadUsage()}
                              disabled={usageLoading}
                            >
                              Frissítés
                            </Button>
                          </Stack>
                          {(usage.series ?? []).length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Még nincs napi összesítő. Az API forgalom és a
                              napi snapshot után jelennek meg a sorok.
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                  'minmax(100px, 1.2fr) repeat(3, minmax(80px, 1fr))',
                                gap: 1,
                                alignItems: 'center',
                                maxHeight: 420,
                                overflowY: 'auto',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Nap
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Mobil API
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Aktív eszköz
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Tárhely
                              </Typography>
                              {[...(usage.series ?? [])].map((day: any) => (
                                  <Box
                                    key={day.date}
                                    sx={{
                                      display: 'contents',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {formatUsageDate(day.date)}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {formatCount(day.apiCallsMobile)}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {formatCount(day.activeDevices)}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        py: 0.75,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {formatBytes(
                                        day.storageBytes ?? day.StorageBytes
                                      )}
                                    </Typography>
                                  </Box>
                                ))}
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </>
                  );
                })()}
              </Grid>
            )}
            {!usageLoading && !usage && !usageError && (
              <Typography color="text.secondary">
                Nincs használati adat.
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default TenantOverviewPage;
